import { useMemo, useState } from "react";
import { SimpleGrid, Button, Skeleton, useDisclosure, useToast } from "@chakra-ui/react";
import { PageHeader, EmptyState, SectionCard } from "../../../shared/ui";
import { Plus, Building2 } from "../../../shared/icons";
import { useVinculosQuery } from "../hooks/useVinculosQuery";
import { useSetVinculoFavoritoMutation } from "../hooks/useSetVinculoFavoritoMutation";
import { sortFavoritesFirst } from "../utils/sortFavoritesFirst";
import { apiErrorMessage } from "../../../shared/api/apiErrorMessage";
import VinculoCard from "../components/VinculoCard";
import NovoVinculoModal from "../components/NovoVinculoModal";
import DeleteVinculoDialog from "../components/DeleteVinculoDialog";

export default function VinculosListPage() {
  const toast = useToast();
  const { data, isLoading } = useVinculosQuery();
  const vinculos = useMemo(() => sortFavoritesFirst(data ?? []), [data]);
  const favoritoMutation = useSetVinculoFavoritoMutation();
  const alternarFavorito = (vinculo) =>
    favoritoMutation.mutate(
      { id: vinculo.id, favorito: !vinculo.favorito },
      {
        onError: (error) =>
          toast({
            title: "Não foi possível atualizar o favorito",
            description: apiErrorMessage(error),
            status: "error",
            duration: 5000,
            isClosable: true,
          }),
      }
    );
  const novoVinculo = useDisclosure();
  const [vinculoEditando, setVinculoEditando] = useState(null);
  const [vinculoExcluindo, setVinculoExcluindo] = useState(null);

  return (
    <>
      <PageHeader
        title="Vínculos"
        description="Hospitais, cooperativas e OS com quem você trabalha"
        actions={
          <Button leftIcon={<Plus size={16} />} onClick={novoVinculo.onOpen}>
            Novo vínculo
          </Button>
        }
      />

      {isLoading ? (
        <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={5}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} height="280px" borderRadius="xl" />
          ))}
        </SimpleGrid>
      ) : vinculos.length === 0 ? (
        <SectionCard>
          <EmptyState
            icon={Building2}
            title="Nenhum vínculo cadastrado"
            description="Cadastre o primeiro hospital, cooperativa ou OS para começar a registrar seus plantões."
            action={
              <Button leftIcon={<Plus size={16} />} onClick={novoVinculo.onOpen}>
                Cadastrar vínculo
              </Button>
            }
          />
        </SectionCard>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={5}>
          {vinculos.map((vinculo) => (
            <VinculoCard
              key={vinculo.id}
              vinculo={vinculo}
              onEdit={setVinculoEditando}
              onDelete={setVinculoExcluindo}
              onToggleFavorite={alternarFavorito}
            />
          ))}
        </SimpleGrid>
      )}

      <NovoVinculoModal isOpen={novoVinculo.isOpen} onClose={novoVinculo.onClose} />
      <NovoVinculoModal
        isOpen={Boolean(vinculoEditando)}
        onClose={() => setVinculoEditando(null)}
        vinculo={vinculoEditando}
      />
      <DeleteVinculoDialog
        isOpen={Boolean(vinculoExcluindo)}
        onClose={() => setVinculoExcluindo(null)}
        vinculo={vinculoExcluindo}
      />
    </>
  );
}
